import React from 'react';
import I18n from 'i18n-js';
import { RouteComponentProps } from 'react-router';
import gql from 'graphql-tag';
import { graphql, ChildProps } from 'react-apollo';

import ShopReturnPolicies, { ShopReturnPoliciesFragment } from './shop_return_policies';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import VideoEmbed from '@reverbdotcom/commons/src/components/video_embed';
import { CoreShopInfo } from '../../../schemas/rql';
import { MarketplacePathContext } from '../../shared/url_helpers';
import ShopTaxPolicies, { ShopTaxPoliciesFragment } from './shop_tax_policies';

type RouteProps = RouteComponentProps<MarketplacePathContext, {}>;
type Props = RouteProps & ChildProps<RouteProps, CoreShopInfo.Query, CoreShopInfo.Variables>;

export class ShopInfo extends React.Component<Props, null> {
  renderBio() {
    const { shop } = this.props.data;
    if (!shop.description && !shop.website) return null;
    return (
      <div className="scaling-padding-3 bg-white">
        <div className="size-120 weight-bold mb-1">
          {I18n.t('discovery.shopInfo.about', { name: shop.name })}
        </div>
        {!!shop.description &&
          <SanitizedRender
            html={shop.description}
            htmlTag="p"
            className="mb-0"
            convertNewlines
          />
        }
        {!!shop.website &&
          <p
            className="mb-0 scaling-mt-2"
          >
            <a
              href={shop.website}
              target="_blank"
              rel="noopener noreferrer"
              className="icon-r-external-link"
            >
              {I18n.t('discovery.shopInfo.shopWebsite')}
            </a>
          </p>
        }
      </div>
    );
  }

  renderVideo() {
    const { shop } = this.props.data;
    if (!shop?.video || shop.video.empty) return null;

    return (
      <div className="scaling-mb-2">
        <VideoEmbed
          slug={shop.video.youtubeVideoId}
        />
      </div>
    );
  }

  renderPlaceholder() {
    return (
      <div className="site-wrapper scaling-mb-4 opacity-50">
        <div className="g-container g-container--cycle">
          <div className="g-col-5 g-col-tablet-6 g-col-mobile-12">
            <div className="scaling-padding-10 bg-white" />
          </div>
          <div className="g-col-7 g-col-tablet-6 g-col-mobile-12">
            <div className="scaling-padding-10 bg-white" />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { shop } = this.props.data;
    if (!shop) return this.renderPlaceholder();

    return (
      <div className="site-wrapper scaling-mb-4">
        <div className="g-container g-container--cycle">
          <div className="g-col-5 g-col-tablet-6 g-col-mobile-12">
            {this.renderVideo()}
            {this.renderBio()}
          </div>
          <div className="g-col-7 g-col-tablet-6 g-col-mobile-12">
            <div className="scaling-padding-3 scaling-mb-2 bg-white">
              <h3 className="cms-h3 weight-bold mt-0">
                {I18n.t('discovery.shopInfo.shippingPolicy')}
              </h3>
              <SanitizedRender
                html={shop.shippingPolicy}
                htmlTag="p"
                className="mb-0"
                convertNewlines
              />
            </div>
            <div className="scaling-padding-3 scaling-mb-2 bg-white">
              <h3 className="cms-h3 weight-bold mt-0">
                {I18n.t('discovery.shopInfo.returnPolicy')}
              </h3>
              <ShopReturnPolicies shop={this.props.data.shop} />
            </div>
            <div className="scaling-padding-3 bg-white">
              <h3 className="cms-h3 weight-bold mt-0">
                {I18n.t('discovery.shopInfo.taxPolicy')}
              </h3>
              <ShopTaxPolicies shop={this.props.data.shop} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const connect = graphql<
RouteProps,
CoreShopInfo.Query,
CoreShopInfo.Variables
>(
  gql`
    query Core_Shop_Info($shop: String) {
      shop(input: { slug: $shop }) {
        _id
        id
        description
        website
        name
        shippingPolicy
        video {
          youtubeVideoId
          empty
        }
        ... ShopReturnPoliciesFragment
        ... ShopTaxPoliciesData
      }
    }
    ${ShopReturnPoliciesFragment}
    ${ShopTaxPoliciesFragment}
  `,
  {
    options: (props) => ({
      variables: {
        shop: props.params.shop,
      },
    }),
  },
);

export default connect(ShopInfo);
